import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetAttachmentTypeQuery } from "@generated";
import { AttachmentTypeForm } from "../AttachmentTypeForm/AttachmentTypeForm";
import { useAttachmentTypeMutations } from "../api";
import { emptyAttachmentTypeForm } from "../types";
import type { AttachmentTypeFormValues } from "../types";

const FORM_ID = "attachment-type-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useAttachmentTypeMutations();
  const { data, isLoading, refetch } = useV2GetAttachmentTypeQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<AttachmentTypeFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<AttachmentTypeFormValues>(() => {
    const attrs = data?.data?.attributes as Record<string, string> | undefined;
    if (!attrs) return emptyAttachmentTypeForm;
    return {
      name: attrs.name ?? "",
      // Read the kebab wire key (the generated type names it `document_type`).
      documentType: attrs["document-type"] ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: AttachmentTypeFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update attachment type", err);
      setToast({ variant: "danger", text: "Could not save attachment type." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Attachment Type"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <AttachmentTypeForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
